import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExchangeAlt,
  faArrowUp,
  faArrowDown,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faArrowLeft,
  faSearch,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { authGet, authPost } from "../lib/api";
import { useNotification } from "./NotificationContext";

const formatExpiryTime = (raw) => {
  if (!raw) return null;
  try {
    const d = new Date(raw.replace(" ", "T"));
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Jakarta",
    }) + " WIB";
  } catch {
    return raw;
  }
};

/**
 * CategoryTransferView
 * Embedded view for category transfer flow (replacing modal)
 */
export const CategoryTransferView = ({ orders, onTransferComplete, eventConfigs = {} }) => {
  const { showNotification } = useNotification();
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // State for transfer logic
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState("");
  const [isSnapReady, setIsSnapReady] = useState(false);
  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [checkingPending, setCheckingPending] = useState(false);

  // Filter eligible orders
  const eligibleOrders = orders.filter(
    (item) => 
      item.transaction_status === "settlement" && 
      item.type !== "shuttle" && 
      item.kategori
  );

  // Check Snap readiness
  useEffect(() => {
    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);
    return () => clearInterval(checkSnap);
  }, []);

  // Fetch categories and check pending transfer when order selected
  useEffect(() => {
    if (selectedOrder) {
      fetchCategories();
      setSelectedCategory("");
      setCalculation(null);
      setError("");
      checkExistingPending();
    } else {
      setPendingTransfer(null);
    }
  }, [selectedOrder]);

  const checkExistingPending = useCallback(async () => {
    if (!selectedOrder) return;
    setCheckingPending(true);
    try {
      const response = await authGet(`/api/category-transfer/pending/${selectedOrder.id}`);
      if (response.data.hasPending) {
        // Validate the Snap token is still alive with Midtrans before showing pending view
        try {
          const refreshRes = await authGet(`/api/category-transfer/refresh/${response.data.transferId}`);
          const { status } = refreshRes.data;
          if (status === "settlement" || status === "capture") {
            setPendingTransfer(null);
            onTransferComplete?.();
          } else if (status === "pending") {
            setPendingTransfer(response.data);
          } else {
            // expired / cancel / deny — don't show the stuck pending view
            setPendingTransfer(null);
          }
        } catch (refreshErr) {
          // If refresh fails, fall back to showing the pending view
          console.warn("Transfer refresh check failed, showing pending view:", refreshErr.message);
          setPendingTransfer(response.data);
        }
      } else {
        setPendingTransfer(null);
      }
    } catch (err) {
      console.error("Failed to check pending transfer:", err);
      setPendingTransfer(null);
    } finally {
      setCheckingPending(false);
    }
  }, [selectedOrder, onTransferComplete]);

  const fetchCategories = useCallback(async () => {
    if (!selectedOrder) return;
    try {
      const response = await authGet(
        `/api/category-transfer/categories/${selectedOrder.eventId}`
      );
      const availableCategories = response.data.categories.filter(
        (cat) => cat.categoryName !== selectedOrder.kategori
      );
      setCategories(availableCategories);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      setError("Failed to load available categories");
    }
  }, [selectedOrder]);

  // Calculate transfer cost
  useEffect(() => {
    if (selectedCategory && selectedOrder) {
      calculateTransfer();
    } else {
      setCalculation(null);
    }
  }, [selectedCategory]);

  const calculateTransfer = async () => {
    setCalculating(true);
    setError("");
    try {
      const response = await authPost(
        "/api/category-transfer/calculate",
        {
          orderId: selectedOrder.id,
          newCategory: selectedCategory,
        }
      );
      setCalculation(response.data);
    } catch (err) {
      console.error("Failed to calculate transfer:", err);
      setError(err.response?.data?.message || "Failed to calculate transfer cost");
      setCalculation(null);
    } finally {
      setCalculating(false);
    }
  };

  const openSnapWithTransfer = useCallback((snapToken, transferId) => {
    window.snap.pay(snapToken, {
      onSuccess: async () => {
        try {
          await authPost("/api/category-transfer/complete", { transferId });
          showNotification("success", "Category transfer completed!");
          setPendingTransfer(null);
          setSelectedOrder(null);
          onTransferComplete?.();
        } catch (completeErr) {
          console.error("Failed to complete transfer:", completeErr);
          showNotification("error", "Payment successful but transfer update failed.");
        }
      },
      onPending: () => {
        showNotification("warning", "Payment pending. Please complete your payment.");
        onTransferComplete?.();
      },
      onError: () => {
        showNotification("error", "Payment failed.");
        setPendingTransfer(null);
      },
      onClose: async () => {
        setLoading(false);
        if (!transferId) return;
        try {
          const refreshRes = await authGet(`/api/category-transfer/refresh/${transferId}`);
          const { status } = refreshRes.data;
          if (status === "settlement" || status === "capture") {
            showNotification("success", "Category transfer completed!");
            setPendingTransfer(null);
            setSelectedOrder(null);
            onTransferComplete?.();
          } else if (status !== "pending") {
            setPendingTransfer(null);
          }
        } catch (refreshErr) {
          console.warn("Transfer status refresh failed:", refreshErr.message);
        }
      },
    });
  }, [showNotification, onTransferComplete]);

  const handleResumePayment = useCallback(() => {
    if (!pendingTransfer?.snapToken || !isSnapReady) return;
    openSnapWithTransfer(pendingTransfer.snapToken, pendingTransfer.transferId);
  }, [pendingTransfer, isSnapReady, openSnapWithTransfer]);

  const handleTransfer = async () => {
    if (!calculation) return;

    setLoading(true);
    setError("");

    try {
      const response = await authPost(
        "/api/category-transfer/execute",
        {
          orderId: selectedOrder.id,
          newCategory: selectedCategory,
          paymentMethod: "midtrans",
        }
      );

      // Downgrade - completed immediately
      if (response.data.transferType === "downgrade") {
        showNotification("success", "Category changed successfully!");
        onTransferComplete?.();
        setSelectedOrder(null);
        return;
      }

      // Upgrade - need payment
      if (response.data.snapToken && isSnapReady) {
        const transferInfo = {
          transferId: response.data.transferId,
          snapToken: response.data.snapToken,
          amountDue: response.data.amountDue,
          newCategory: selectedCategory,
          oldCategory: selectedOrder.kategori,
        };
        setPendingTransfer(transferInfo);
        openSnapWithTransfer(response.data.snapToken, response.data.transferId);
      } else {
        setError("Payment gateway is not ready.");
      }
    } catch (err) {
      console.error("Failed to execute transfer:", err);
      setError(err.response?.data?.message || "Transfer failed");
    } finally {
      setLoading(false);
    }
  };

  // View: Pending Transfer
  if (selectedOrder && pendingTransfer) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-4">
          <button
            onClick={() => { setPendingTransfer(null); setSelectedOrder(null); }}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <h2 className="text-lg font-bold text-slate-800">Pending Payment</h2>
            <p className="text-sm text-slate-600">
              {selectedOrder.eventId} • {selectedOrder.namaLengkap}
            </p>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
            <FontAwesomeIcon icon={faClock} className="text-amber-500 text-xl flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Awaiting Payment</p>
              <p className="text-sm text-amber-700">
                Your category transfer request is pending. Complete the payment to apply the change.
              </p>
            </div>
          </div>

          {/* VA / Payment Method Details - shown after user selects payment method in Snap */}
          {(pendingTransfer.va_number_first || pendingTransfer.bill_key) && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl mb-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Payment Details</p>
              {pendingTransfer.va_number_first && (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Bank</span>
                    <span className="font-semibold text-slate-900 uppercase">{pendingTransfer.va_bank_first || "-"}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Virtual Account</span>
                    <span className="font-mono font-bold text-slate-900 tracking-wider">{pendingTransfer.va_number_first}</span>
                  </div>
                </div>
              )}
              {!pendingTransfer.va_number_first && pendingTransfer.bill_key && (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Biller Code</span>
                    <span className="font-mono font-bold text-slate-900">{pendingTransfer.biller_code}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Bill Key</span>
                    <span className="font-mono font-bold text-slate-900">{pendingTransfer.bill_key}</span>
                  </div>
                </div>
              )}
              {pendingTransfer.expiry_time && (
                <p className="text-xs text-red-600 font-medium mt-3">
                  Pay before: {formatExpiryTime(pendingTransfer.expiry_time)}
                </p>
              )}
            </div>
          )}

          <div className="border border-slate-200 rounded-xl p-4 mb-6 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Transfer ID</span>
              <span className="font-mono text-xs font-medium text-slate-700">{pendingTransfer.transferId}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">From Category</span>
              <span className="font-medium text-slate-900">{pendingTransfer.oldCategory || selectedOrder.kategori}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">To Category</span>
              <span className="font-medium text-slate-900">{pendingTransfer.newCategory}</span>
            </div>
            <div className="flex justify-between text-sm border-t border-slate-100 pt-3">
              <span className="font-bold text-slate-700">Amount Due</span>
              <span className="font-bold text-slate-900">
                Rp {pendingTransfer.amountDue?.toLocaleString("id-ID")}
              </span>
            </div>
          </div>

          <button
            onClick={handleResumePayment}
            disabled={!isSnapReady}
            className="w-full py-3 px-4 rounded-xl font-bold text-white bg-slate-700 hover:bg-slate-800 transition-all text-sm disabled:bg-slate-400 disabled:cursor-not-allowed"
          >
            {!isSnapReady ? (
              <><FontAwesomeIcon icon={faSpinner} spin className="mr-2" />Loading...</>
            ) : (
              "Resume Payment"
            )}
          </button>
        </div>
      </div>
    );
  }

  // View: List of Eligible Orders
  if (!selectedOrder) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-800">Change Category</h2>
          <p className="text-sm text-slate-600 mt-1">
            Select a registration to upgrade or downgrade your category.
          </p>
        </div>

        {eligibleOrders.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-4">
              <FontAwesomeIcon icon={faSearch} className="text-sm text-slate-500" />
            </div>
            <p className="font-medium text-slate-800">No eligible orders found</p>
            <p className="text-sm mt-1">Only settled event registrations can be transferred.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {eligibleOrders.map((item) => (
              <div key={item.id} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-slate-50 transition-colors">
                <div>
                  <h3 className="font-bold text-slate-900">{item.eventId || "Event"}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm font-medium text-slate-700">{item.kategori}</span>
                    <span className="text-xs text-slate-400">•</span>
                    <span className="text-sm text-slate-600">{item.namaLengkap}</span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedOrder(item)}
                  className="px-4 py-2 bg-white text-slate-800 font-medium text-sm border border-slate-300 rounded-xl hover:bg-slate-50 transition-all"
                >
                  Select Registration
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Loading check while verifying pending transfer
  if (checkingPending) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mb-2" />
        <p className="text-sm">Checking transfer status...</p>
      </div>
    );
  }

  // VALIDATION CHECK
  const config = selectedOrder ? eventConfigs[selectedOrder.eventId] : null;
  // Default to false (disabled) if no config found, or respect config
  const isEnabled = config?.enabled === true; 
  const pDate = (d) => {
    if (!d) return null;
    // Handle YYYY-MM-DD
    if (d.includes("-")) {
      const date = new Date(d);
      date.setHours(23, 59, 59, 999);
      return date;
    }
    // Handle potential DD/MM/YYYY
    if (d.includes("/")) {
      const [day, m, y] = d.split("/");
      const date = new Date(`${y}-${m}-${day}T23:59:59`);
      return date;
    }
    return new Date(d);
  };

  const isDeadlinePassed = config?.deadline ? new Date() > pDate(config.deadline) : false;
  // If no config loaded yet? Maybe show loading? existing loading logic might handle it.
  // Assuming if config is missing (undefined), it is disabled.
  
  if (!isEnabled || isDeadlinePassed) {
      return (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center gap-4">
            <button 
              onClick={() => setSelectedOrder(null)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Change Category</h2>
              <p className="text-sm text-slate-600">
                {selectedOrder.eventId} • {selectedOrder.namaLengkap}
              </p>
            </div>
          </div>
          <div className="p-12 text-center">
             <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500 text-2xl" />
             </div>
             <h3 className="text-xl font-bold text-slate-800 mb-2">
                {isDeadlinePassed ? "Deadline Passed" : "Feature Unavailable"}
             </h3>
             <p className="text-slate-600 max-w-md mx-auto">
                {isDeadlinePassed 
                   ? `The period for changing category ended on ${new Date(config.deadline).toLocaleDateString()}.` 
                   : "Category transfer is currently disabled for this event. Please contact admin for assistance."}
             </p>
          </div>
        </div>
      );
  }

  // View: Transfer Form (Embedded)
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 flex items-center gap-4">
        <button 
          onClick={() => setSelectedOrder(null)}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div>
          <h2 className="text-lg font-bold text-slate-800">Change Category</h2>
          <p className="text-sm text-slate-600">
            {selectedOrder.eventId} • {selectedOrder.namaLengkap}
          </p>
        </div>
      </div>

      <div className="p-4 grid md:grid-cols-2 gap-4">
        {/* Left Column: Form */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Select New Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-xl focus:ring-1 focus:ring-slate-700 focus:border-slate-700 transition-all bg-white text-slate-800 text-sm"
              disabled={loading}
            >
              <option value="">-- Choose Category --</option>
              {categories.map((cat) => (
                <option key={cat.categoryName} value={cat.categoryName}>
                  {cat.categoryName} - Rp {cat.price.toLocaleString("id-ID")}
                </option>
              ))}
            </select>
          </div>

          <div className="border border-slate-200 rounded-xl p-4">
            <h4 className="text-sm font-medium text-slate-800 mb-2">Current Registration</h4>
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500">Category</span>
              <span className="font-medium text-slate-900">{selectedOrder.kategori}</span>
            </div>
            <div className="flex justify-between items-center text-sm mt-1">
              <span className="text-slate-500">Status</span>
              <span className="font-medium text-slate-900">Settled (Paid)</span>
            </div>
          </div>

          {error && (
            <div className="border border-slate-200 bg-slate-50 text-slate-800 p-4 rounded-xl text-sm flex items-start gap-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Right Column: Summary & Pricing */}
        <div className="border border-slate-200 rounded-xl p-4 h-fit mt-7">
          <h3 className="font-bold text-slate-800 mb-4">Cost Summary</h3>
          
          {!calculation ? (
            <div className="text-center py-4 text-slate-400 text-sm">
              {calculating ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="mb-2" />
                  <p>Calculating cost...</p>
                </>
              ) : (
                "Select a category to view pricing"
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border border-slate-200 text-slate-700">
                <FontAwesomeIcon icon={calculation.type === "upgrade" ? faArrowUp : faArrowDown} className="text-xs" />
                {calculation.type === "upgrade" ? "Upgrade Request" : "Downgrade Request"}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Current Price</span>
                  <span>Rp {calculation.currentPrice?.toLocaleString("id-ID")}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>New Price</span>
                  <span className="font-medium text-slate-900">Rp {calculation.newPrice?.toLocaleString("id-ID")}</span>
                </div>
                
                {calculation.type === "upgrade" && (
                  <>
                    <div className="flex justify-between text-slate-600">
                      <span>Price Difference</span>
                      <span>Rp {calculation.priceDifference?.toLocaleString("id-ID")}</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Admin Fee</span>
                      <span>Rp {calculation.adminFee?.toLocaleString("id-ID")}</span>
                    </div>
                  </>
                )}
                
                <div className="border-t border-slate-200 my-2 pt-2"></div>
                
                <div className="flex justify-between items-end">
                  <span className="font-bold text-slate-700">Total to Pay</span>
                  <span className="text-xl font-bold text-slate-900">
                    {calculation.amountDue > 0 
                      ? `Rp ${calculation.amountDue.toLocaleString("id-ID")}` 
                      : "Rp 0"}
                  </span>
                </div>

                {calculation.type === "downgrade" && (
                  <p className="text-xs text-slate-500 italic mt-1">
                    * No refund will be provided for price difference.
                  </p>
                )}
              </div>

              <button
                onClick={handleTransfer}
                disabled={loading}
                className={`w-full py-3 px-4 rounded-xl font-bold text-white mt-4 text-sm transition-all ${
                  loading 
                    ? "bg-slate-400 cursor-not-allowed" 
                    : "bg-slate-700 hover:bg-slate-800"
                }`}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    Confirm & {calculation.type === "upgrade" ? "Pay" : "Transfer"}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
